import type {
  Candidate,
  Criterion,
  EmploymentType,
  HiringMatrix,
  Industry,
  LocationType,
  Position,
  Seniority,
} from "../types";

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = "";
    try {
      detail = JSON.stringify(await res.json());
    } catch {
      detail = await res.text();
    }
    throw new Error(`${res.status} ${res.statusText}: ${detail}`);
  }
  return (await res.json()) as T;
}

export const api = {
  // --- Positions ---
  listPositions: () =>
    fetch("/api/positions").then((r) => handle<{ positions: Position[] }>(r)),

  createPosition: (body: {
    title: string;
    description?: string;
    seniority?: Seniority;
    industry?: Industry;
    employment_type?: EmploymentType;
    location?: LocationType;
  }) =>
    fetch("/api/positions", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => handle<{ position: Position }>(r)),

  deletePosition: (id: number) =>
    fetch(`/api/positions/${id}`, { method: "DELETE" }).then((r) =>
      handle<{ ok: true }>(r)
    ),

  // --- Hiring Matrices ---
  listMatrices: (positionId?: number) =>
    fetch(
      positionId ? `/api/matrices?position_id=${positionId}` : "/api/matrices"
    ).then((r) => handle<{ matrices: HiringMatrix[] }>(r)),

  createMatrix: (body: {
    position_id: number;
    name: string;
    criteria: Criterion[];
  }) =>
    fetch("/api/matrices", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => handle<{ matrix: HiringMatrix }>(r)),

  deleteMatrix: (id: number) =>
    fetch(`/api/matrices/${id}`, { method: "DELETE" }).then((r) =>
      handle<{ ok: true }>(r)
    ),

  // --- Candidates ---
  listCandidates: (opts?: {
    positionId?: number;
    matrixId?: number;
    archived?: boolean;
  }) => {
    const params = new URLSearchParams();
    if (opts?.matrixId) params.set("matrix_id", String(opts.matrixId));
    else if (opts?.positionId) params.set("position_id", String(opts.positionId));
    if (opts?.archived) params.set("archived", "true");
    const qs = params.toString();
    return fetch(`/api/candidates${qs ? `?${qs}` : ""}`).then((r) =>
      handle<{ candidates: Candidate[] }>(r)
    );
  },

  archiveCandidate: (id: number, archived: boolean) =>
    fetch(`/api/candidates/${id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ archived }),
    }).then((r) => handle<{ ok: true }>(r)),

  deleteCandidate: (id: number) =>
    fetch(`/api/candidates/${id}`, { method: "DELETE" }).then((r) =>
      handle<{ ok: true }>(r)
    ),

  // --- Screen ---
  screen: (body: {
    position_id: number;
    matrix_id: number;
    filename: string;
    resume_text: string;
    photo?: string | null;
  }) =>
    fetch("/api/screen", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => handle<{ candidate: Candidate }>(r)),
};
